'use client';

/**
 * Data + filter state hook for the courses listing page.
 *
 * Module: app/courses/use-courses
 * Layer:  hook
 *
 * Purpose:
 *     Encapsulates course/category fetching and URL-driven filtering for
 *     the listing page so the page component stays small and every
 *     function clears the CC<=5 / <=60-line gates. The URL query string is
 *     the source of truth for the active search/category; sort + view mode
 *     are local UI state.
 *
 * AI context:
 *     - Entry points called by: src/app/courses/page.tsx (CoursesContent).
 *     - Depends on: next/navigation, lib/courses fetchJson.
 *     - Side effects: network I/O, router.push navigation.
 *     - Invariants: data loads keyed off (activeQuery, activeCategory,
 *       sortBy); fetch failures are logged, never thrown to React.
 *     - Replacement cost: LOW.
 */

import type { Route } from 'next';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { type Category, type CourseSummary, fetchJson } from '@/lib/courses';

/** View layout for the results grid. */
export type ViewMode = 'grid' | 'list';

/** Everything the listing page needs to render and react to user input. */
export interface CoursesController {
  readonly courses: readonly CourseSummary[];
  readonly featuredCourses: readonly CourseSummary[];
  readonly categories: readonly Category[];
  readonly loading: boolean;
  readonly activeQuery: string;
  readonly activeCategory: string | null;
  readonly draftQuery: string;
  readonly sortBy: string;
  readonly viewMode: ViewMode;
  readonly isHomepage: boolean;
  readonly setDraftQuery: (next: string) => void;
  readonly setSortBy: (next: string) => void;
  readonly setViewMode: (next: ViewMode) => void;
  readonly submitSearch: () => void;
  readonly selectCategory: (slug: string | null) => void;
  readonly clearFilters: () => void;
}

/** Result shape returned by the courses search endpoint. */
interface CoursesResponse {
  readonly courses: readonly CourseSummary[];
}

/** Build the query string for the search endpoint from active filters. */
function buildSearchParams(query: string, category: string | null, sortBy: string): string {
  const params = new URLSearchParams();
  if (query) {
    params.set('search', query);
  }
  if (category) {
    params.set('category', category);
  }
  params.set('sort_by', sortBy);
  return params.toString();
}

/** Build the navigation URL that reflects the active filters. */
function buildNavUrl(query: string, category: string | null): Route {
  const params = new URLSearchParams();
  if (query) {
    params.set('q', query);
  }
  if (category) {
    params.set('category', category);
  }
  const qs = params.toString();
  return (qs ? `/courses?${qs}` : '/courses') as Route;
}

/** Fetch featured courses, or none when a filter is active. */
async function loadFeatured(hasFilter: boolean): Promise<readonly CourseSummary[]> {
  if (hasFilter) {
    return [];
  }
  return fetchJson<readonly CourseSummary[]>('/api/courses/featured');
}

/** Loaded listing data plus its in-flight flag. */
interface CoursesData {
  readonly courses: readonly CourseSummary[];
  readonly featuredCourses: readonly CourseSummary[];
  readonly categories: readonly Category[];
  readonly loading: boolean;
}

/**
 * Load courses, featured courses, and categories for the active filters.
 *
 * @param query - Active search term from the URL.
 * @param category - Active category slug, or null.
 * @param sortBy - Active sort key.
 * @returns The loaded data plus a loading flag.
 * @remarks Cancels stale responses on dependency change to avoid races.
 */
function useCoursesData(query: string, category: string | null, sortBy: string): CoursesData {
  const [courses, setCourses] = useState<readonly CourseSummary[]>([]);
  const [featuredCourses, setFeaturedCourses] = useState<readonly CourseSummary[]>([]);
  const [categories, setCategories] = useState<readonly Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      setLoading(true);
      const hasFilter = Boolean(query) || Boolean(category);
      try {
        const qs = buildSearchParams(query, category, sortBy);
        const [list, featured, cats] = await Promise.all([
          fetchJson<CoursesResponse>(`/api/courses?${qs}`),
          loadFeatured(hasFilter),
          fetchJson<readonly Category[]>('/api/courses/categories'),
        ]);
        if (!cancelled) {
          setCourses(list.courses);
          setFeaturedCourses(featured);
          setCategories(cats);
        }
      } catch (error) {
        console.error('Failed to load courses:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void run();
    return (): void => {
      cancelled = true;
    };
  }, [query, category, sortBy]);

  return { courses, featuredCourses, categories, loading };
}

export function useCourses(): CoursesController {
  const router = useRouter();
  const searchParams = useSearchParams();
  const activeQuery = searchParams.get('q') ?? '';
  const activeCategory = searchParams.get('category');

  const [sortBy, setSortBy] = useState('popular');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const data = useCoursesData(activeQuery, activeCategory, sortBy);

  // Draft input value tracks the URL query but stays locally editable.
  const [draft, setDraft] = useState<{ syncedTo: string; value: string }>({
    syncedTo: activeQuery,
    value: activeQuery,
  });
  // WHY: React-recommended "adjust state during render" pattern resyncs the
  // draft when the URL query changes externally, avoiding setState-in-effect.
  if (draft.syncedTo !== activeQuery) {
    setDraft({ syncedTo: activeQuery, value: activeQuery });
  }

  const submitSearch = useCallback((): void => {
    router.push(buildNavUrl(draft.value, activeCategory));
  }, [router, draft.value, activeCategory]);

  const selectCategory = useCallback(
    (slug: string | null): void => {
      router.push(buildNavUrl(draft.value, slug));
    },
    [router, draft.value],
  );

  const clearFilters = useCallback((): void => {
    router.push('/courses');
  }, [router]);

  const setDraftQuery = useCallback((next: string): void => {
    setDraft((prev) => ({ syncedTo: prev.syncedTo, value: next }));
  }, []);

  return {
    courses: data.courses,
    featuredCourses: data.featuredCourses,
    categories: data.categories,
    loading: data.loading,
    activeQuery,
    activeCategory,
    draftQuery: draft.value,
    sortBy,
    viewMode,
    isHomepage: !activeQuery && !activeCategory,
    setDraftQuery,
    setSortBy,
    setViewMode,
    submitSearch,
    selectCategory,
    clearFilters,
  };
}
